import { invalidatePolicyViews } from "../../../lib/policyQueries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { LIVE_REFRESH_INTERVAL_MS } from "../../../lib/useLiveNow";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card } from "../../../components/ui/Card";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { FilterBar } from "../../../components/ui/FilterBar";
import { Input } from "../../../components/ui/Input";
import { MasterDetailLayout } from "../../../components/ui/MasterDetailLayout";
import { PageHeader } from "../../../components/ui/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "../../../components/ui/State";
import { type Column, Table } from "../../../components/ui/Table";
import { ApiError } from "../../../lib/api";
import { type WorkScheduleProfile, schedulesApi } from "../../../lib/api/policies";
import { formatDateTime } from "../../../lib/format";
import { formatMessage, t } from "../../../lib/i18n";
import { useToast } from "../../../components/ui/Toast";
import { useLocale } from "../../../lib/useLocale";
import { ScheduleEditorPage } from "./ScheduleEditorPage";
import { SchedulesKpiStrip } from "./components/SchedulesKpiStrip";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function formatAssignmentsSummary(row: WorkScheduleProfile): string {
  if (row.assignments_count > 0) {
    return formatMessage("schedules.list.assignmentsCount", {
      count: String(row.assignments_count),
    });
  }
  if (row.is_company_fallback) {
    return t("schedules.list.companyDefault");
  }
  return "—";
}

export function SchedulesListPage() {
  useLocale();
  const qc = useQueryClient();
  const { push } = useToast();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [active, setActive] = useState<"" | "true" | "false">("");
  const [target, setTarget] = useState<WorkScheduleProfile | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<WorkScheduleProfile | null>(null);

  const selectedId = params.get("id");

  const openDetail = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("id", id);
    setParams(next, { replace: false });
  };
  const closeDetail = () => {
    const next = new URLSearchParams(params);
    next.delete("id");
    setParams(next, { replace: false });
  };

  const list = useQuery({
    refetchInterval: LIVE_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: "always",
    queryKey: ["schedules", search, active],
    queryFn: () =>
      schedulesApi.list({
        search: search || undefined,
        is_active: active === "" ? undefined : active === "true",
      }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => schedulesApi.delete(id),
    onSuccess: (_, id) => {
      setTarget(null);
      if (selectedId === id) closeDetail();
      qc.removeQueries({ queryKey: ["schedule", id], exact: true });
      void invalidatePolicyViews(qc);
      push({ tone: "success", title: t("schedules.list.deleteSuccess") });
    },
    onError: (err) => {
      push({ tone: "danger", title: translateError(err) });
    },
  });

  const duplicate = useMutation({
    onSettled: () => invalidatePolicyViews(qc),
    mutationFn: async (profile: WorkScheduleProfile) => {
      const detail = await schedulesApi.get(profile.id);
      const copy = await schedulesApi.create({
        name: formatMessage("schedules.list.copyOf", { name: detail.name }, `Copy of ${detail.name}`),
        description: detail.description,
        timezone: detail.timezone,
        schedule_direction: detail.schedule_direction,
        is_active: detail.is_active,
      });
      for (const w of detail.windows) {
        await schedulesApi.createWindow(copy.id, {
          weekday: w.weekday,
          start_minute: w.start_minute,
          end_minute: w.end_minute,
          start_notification_title: w.start_notification_title,
          start_notification_body: w.start_notification_body,
          end_notification_title: w.end_notification_title,
          end_notification_body: w.end_notification_body,
        });
      }
      return copy;
    },
    onSuccess: (copy) => {
      setDuplicateTarget(null);
      void invalidatePolicyViews(qc);
      push({ tone: "success", title: t("schedules.list.duplicateSuccess") });
      openDetail(copy.id);
    },
    onError: (err) => {
      push({ tone: "danger", title: translateError(err) });
    },
  });

  const columns: Column<WorkScheduleProfile>[] = [
    {
      key: "name",
      header: t("schedules.list.column.name"),
      render: (row) => (
        <button
          type="button"
          onClick={() => openDetail(row.id)}
          className="app-name--link"
          style={{
            background: "none",
            border: 0,
            padding: 0,
            cursor: "pointer",
            color: "var(--color-accent)",
            font: "inherit",
            textAlign: "left",
          }}
        >
          {row.name}
        </button>
      ),
    },
    {
      key: "tz",
      header: t("schedules.list.column.timezone"),
      render: (row) => row.timezone,
    },
    {
      key: "direction",
      header: t("schedules.list.column.direction"),
      render: (row) => (
        <Badge tone={row.schedule_direction === "inactive_during" ? "warning" : "neutral"}>
          {t(`scheduleDirection.${row.schedule_direction}`)}
        </Badge>
      ),
    },
    {
      key: "windows",
      header: t("schedules.list.column.windows"),
      render: (row) => row.windows_count,
      align: "right",
    },
    {
      key: "assignments",
      header: t("schedules.list.column.assignments"),
      render: (row) => formatAssignmentsSummary(row),
    },
    {
      key: "effect",
      header: t("schedules.list.column.effect"),
      render: (row) => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {row.is_currently_enforcing && (
            <Badge tone="warning">{t("schedules.list.enforcingNow")}</Badge>
          )}
          {row.is_company_fallback && (
            <Badge tone="info">{t("schedules.list.defaultBadge")}</Badge>
          )}
          {!row.is_currently_enforcing && !row.is_company_fallback && "—"}
        </div>
      ),
    },
    {
      key: "active",
      header: t("schedules.list.column.status"),
      render: (row) =>
        row.is_active ? (
          <Badge tone="success">{t("common.active")}</Badge>
        ) : (
          <Badge tone="neutral">{t("common.inactive")}</Badge>
        ),
    },
    {
      key: "updated",
      header: t("schedules.list.column.updatedAt"),
      render: (row) => formatDateTime(row.updated_at),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button variant="secondary" size="sm" onClick={() => openDetail(row.id)}>
            {t("common.view")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDuplicateTarget(row)}>
            {t("common.duplicate")}
          </Button>
          <Button variant="danger" size="sm" onClick={() => setTarget(row)}>
            {t("common.delete")}
          </Button>
        </div>
      ),
      align: "right",
    },
  ];

  const listContent = (
    <div>
      <PageHeader
        title={t("schedules.list.title")}
        subtitle={t("schedules.list.subtitle")}
        actions={
          <Button onClick={() => openDetail("new")}>
            {t("schedules.list.action.create")}
          </Button>
        }
      />

      <div className="stack">
        {list.data && list.data.length > 0 && (
          <SchedulesKpiStrip rows={list.data} />
        )}
        <FilterBar>
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("common.searchPlaceholder")}
            style={{ width: 220 }}
          />
          <span className="filter-bar__label">{t("schedules.list.column.status")}</span>
          <select
            className="input"
            value={active}
            onChange={(e) => setActive(e.target.value as typeof active)}
          >
            <option value="">{t("common.all")}</option>
            <option value="true">{t("common.active")}</option>
            <option value="false">{t("common.inactive")}</option>
          </select>
        </FilterBar>

        <Card padding="none">
          {list.isLoading && <LoadingState />}
          {list.error && (
            <ErrorState
              titleKey="states.errorTitle"
              message={translateError(list.error)}
              action={
                <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
                  {t("common.retry")}
                </Button>
              }
            />
          )}
          {list.data && (
            <Table
              rows={list.data}
              columns={columns}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  titleKey="schedules.list.emptyTitle"
                  bodyKey="schedules.list.emptyBody"
                  action={
                    <Button onClick={() => openDetail("new")}>
                      {t("schedules.list.action.create")}
                    </Button>
                  }
                />
              }
            />
          )}
        </Card>
      </div>
    </div>
  );

  return (
    <>
      <MasterDetailLayout
        list={listContent}
        detail={
          selectedId ? (
            <div className="master-detail__panel-body">
              <ScheduleEditorPage
                key={selectedId}
                scheduleId={selectedId}
                onCreated={(newId) => openDetail(newId)}
                onClose={closeDetail}
              />
            </div>
          ) : null
        }
        onClose={closeDetail}
        ariaLabel={t("schedules.editor.titleEdit")}
      />

      <ConfirmDialog
        open={target !== null}
        title={t("schedules.delete.title")}
        body={t("schedules.delete.body")}
        loading={remove.isPending}
        onCancel={() => setTarget(null)}
        onConfirm={() => target && remove.mutate(target.id)}
      />

      <ConfirmDialog
        open={duplicateTarget !== null}
        title={
          duplicateTarget
            ? formatMessage("schedules.editor.duplicateConfirmTitle", {
                name: duplicateTarget.name,
              })
            : ""
        }
        body={t("schedules.editor.duplicateConfirmBody")}
        loading={duplicate.isPending}
        onCancel={() => setDuplicateTarget(null)}
        onConfirm={() => duplicateTarget && duplicate.mutate(duplicateTarget)}
        tone="primary"
        confirmLabel={t("common.duplicate")}
      />
    </>
  );
}
